import { cn } from '../../lib/cn'
import { Crest } from './Crest'

interface MastheadProps {
  eyebrow: string
  /** Rendered as the h1. A trailing `emphasis` is set in fairway italic. */
  title: string
  /**
   * The italic accent the display lines carry everywhere else — `PageHead`'s
   * tail, the hero's "got to go." — as the-club card draws it
   * (`<h1>… keeps <em>your hours.</em></h1>`, the-club.html:186).
   */
  emphasis?: string
  /** Small caps line under the title, e.g. "A Private Virtual Club · Est. MMXXVI". */
  estd?: string
  /** Italic serif line to close, in the club's voice. */
  tagline?: string
  className?: string
}

/**
 * Centred seal-over-title opening — how the interior pages announce themselves.
 * (The homepage uses its own asymmetric hero instead.)
 *
 * Scaled to the-club card's `.mast` (the-club.html:62-68): a 128px medallion,
 * the h1 at `clamp(44px, 5.8vw, 76px)` held to 19ch so it breaks onto two
 * lines at desktop width, the estd line 20px under it, the tagline 24px under
 * that at 20px. The build used to run a size down throughout — 96px seal, 54px
 * one-line h1, 18px tagline — and it read as a section head, not a masthead.
 */
export function Masthead({ eyebrow, title, emphasis, estd, tagline, className }: MastheadProps) {
  return (
    <div className={cn('text-center', className)}>
      <Crest monogram className="mx-auto mb-7.5 size-32" />
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-brass-deep">{eyebrow}</p>
      <h1 className="mx-auto mt-5 max-w-[19ch] font-display text-[clamp(2.75rem,5.8vw,4.75rem)] font-medium leading-[1.04] tracking-[-0.018em] text-pine">
        {title}
        {/* The space lives OUTSIDE the em. A browser's accessibility tree keeps
            the space in "keeps<em> your hours.</em>"; what drops it is
            dom-accessibility-api, the accessible-name computation behind
            testing-library's getByRole, which trims each inline child's text
            before concatenating it — so the name it computes for that markup
            is "keepsyour hours.", and a test asking for the natural name finds
            no heading. `PageHead` keeps the same shape for the same reason. */}
        {emphasis && (
          <>
            {' '}
            <em className="italic text-fairway">{emphasis}</em>
          </>
        )}
      </h1>
      {estd && (
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.3em] text-ink-soft">{estd}</p>
      )}
      {tagline && (
        <p className="mt-6 font-display text-xl italic text-fairway">{tagline}</p>
      )}
    </div>
  )
}
